import type { ReactNode } from 'react'
import { Link, type To } from 'react-router-dom'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Project, TaskCard as TaskCardType, TaskStatus, User } from '@witty-board/shared'
import { sumSizePoints } from '../../lib/size.js'
import { TaskCard } from './TaskCard.js'
import { QuickAddTask } from './QuickAddTask.js'

interface ColumnProps {
  status: TaskStatus
  title: string
  /** Vista de columna (fase 7): pulsar el nombre abre esta lista sola, en detalle. */
  titleTo: To
  /** Controles extra de la cabecera, como la ventana de tiempo de Finalizada (fase 7). */
  headerExtra?: ReactNode
  tasks: TaskCardType[]
  projectsById: Map<string, Project>
  usersById: Map<string, User>
  /** "Mis tareas" (CLAUDE.md §6): la clave del proyecto se ve en cada tarjeta. */
  showProjectKey?: boolean
  /** Sin esto, la columna no ofrece alta rápida: "Mis tareas" no tiene un único proyecto al que asignar la tarea nueva. */
  onCreateTask?: (title: string) => void
  /** Columna destino del arrastre en curso (CLAUDE.md §6):
   * pasa de `--surface` a `--surface-hi` para que se vea con claridad dónde
   * va a caer la tarea (ver docs/DECISIONS.md). */
  isDropTarget?: boolean
  quickAddOpen: boolean
  onRequestQuickAdd: () => void
  onCloseQuickAdd: () => void
  onOpenTask: (taskId: string) => void
}

export function Column({
  status,
  title,
  titleTo,
  headerExtra,
  tasks,
  projectsById,
  usersById,
  showProjectKey = false,
  onCreateTask,
  isDropTarget = false,
  quickAddOpen,
  onRequestQuickAdd,
  onCloseQuickAdd,
  onOpenTask,
}: ColumnProps) {
  const { t } = useTranslation()
  const { setNodeRef } = useDroppable({ id: status })

  return (
    <div className="flex w-[calc(100vw-4rem)] max-w-sm shrink-0 snap-start flex-col gap-2 md:w-72 md:max-w-none">
      <div className="flex shrink-0 items-center justify-between rounded-panel bg-surface px-3 py-2.5">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="text-body font-medium text-text">
            <Link
              to={titleTo}
              title={t('board.column.openList')}
              className="rounded-card hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent max-md:inline-flex max-md:min-h-11 max-md:items-center"
            >
              {title}
            </Link>
          </h2>
          <span className="text-meta text-text-muted">
            {t('board.column.summary', { count: tasks.length, points: sumSizePoints(tasks) })}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {headerExtra}
          {onCreateTask && (
            <button
              type="button"
              onClick={onRequestQuickAdd}
              aria-label={t('board.quickAdd.add')}
              className="flex h-6 w-6 max-md:h-11 max-md:w-11 items-center justify-center rounded-card text-text-muted hover:bg-surface-hi hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div
        ref={setNodeRef}
        // Sin scroll propio (post fase 7): crece con sus tarjetas. `flex-1`
        // iguala la altura de las tres columnas a la más larga, y `min-h-60`
        // deja zona donde soltar aunque todas estén casi vacías.
        className={`flex min-h-60 flex-1 flex-col gap-2 rounded-panel bg-surface p-2 transition-colors ${isDropTarget ? 'bg-surface-hi' : ''}`}
      >
        {onCreateTask && quickAddOpen && (
          <QuickAddTask onSubmit={(title) => onCreateTask(title)} onClose={onCloseQuickAdd} />
        )}

        <SortableContext
          items={tasks.map((task) => task.id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              project={projectsById.get(task.projectId)}
              assignee={task.assigneeId ? usersById.get(task.assigneeId) : undefined}
              showProjectKey={showProjectKey}
              onOpen={() => onOpenTask(task.id)}
            />
          ))}
        </SortableContext>
      </div>
    </div>
  )
}
